import { forwardRef, useImperativeHandle, useState, type ForwardedRef, type ReactElement, type ReactNode } from 'react'
import { Marker, Popup, useMapEvents } from 'react-leaflet'
import type L from 'leaflet'
import { MapEditPanel } from './MapEditPanel'
import { LeafletMap } from '../Map'

interface LatLng {
  lat: number
  lng: number
}

function ClickHandler({ active, onClick }: { active: boolean; onClick: (ll: LatLng) => void }) {
  useMapEvents({ click: (e) => { if (active) onClick({ lat: e.latlng.lat, lng: e.latlng.lng }) } })
  return null
}

export interface DockedMapEditorProps<P extends LatLng> {
  points: P[]
  mapCenter: [number, number]
  mapZoom?: number
  // Polyline/Polygon overlay, rendered by the caller since only it knows
  // which shape (path vs. closed ring) applies.
  renderOverlay?: (points: P[]) => ReactNode
  markerIcon: (point: P, index: number, selected: boolean) => L.DivIcon | L.Icon
  panelTitle: (point: P, index: number) => string
  panelExtraFields?: (point: P, index: number) => ReactNode
  // isPlacement is true only for the first commit of a point just created by
  // onAddPoint (tap-place, first drag, or the panel's typed Primeni) -- that's
  // the caller's cue to recompute the point's order from where it actually
  // landed, since onAddPoint had nowhere real to put it yet (docs: the
  // 2026-09-27 map-editor-snapping handoff's ordering-regression fix).
  // Dragging an already-placed point must NOT reorder it (a small nudge
  // shouldn't reshuffle the ring/path), so isPlacement is false there.
  // Returning a number means the point moved to a new array index (its order
  // put it somewhere else in the sorted list) -- DockedMapEditor re-selects
  // that index so the docked panel keeps following the same point.
  onCommitPoint: (index: number, lat: number, lng: number, isPlacement?: boolean) => number | void
  // Returns the new point's resulting array index (post-sort, if the
  // caller re-sorts on insert) so it can be auto-selected.
  onAddPoint: (lat: number, lng: number) => number
  onDeletePoint?: (index: number) => void
  // Existing infrastructure shown for spatial context while editing
  // something else nearby -- e.g. a well's own dot staying visible while
  // its zone's boundary gets drawn around it. Not part of `points`: no
  // drag, no click-to-select, no onCommitPoint wiring, purely visual (a
  // label shows a read-only popup on click, nothing else happens).
  referenceMarkers?: { key: string; lat: number; lng: number; icon: L.DivIcon | L.Icon; label?: string }[]
}

// Lets a parent modal's own chrome (its footer bar) trigger add-point
// without DockedMapEditor rendering its own floating button over the map
// canvas -- the add flow still needs this component's local selection/
// tap-place state, so the trigger is exposed imperatively rather than
// lifting that state up to the caller.
export interface DockedMapEditorHandle {
  triggerAdd: () => void
}

// Docks a per-point edit panel over a still-visible, still-interactive
// map -- mirrors mobile's map-based point editing (PointEditSheet docked
// over a live MapView) rather than web's previous full-viewport modal
// with a bulk sidebar list. Selection is tracked by array index, not
// object reference: dragging the selected point replaces its object in
// the caller's state (a new object at the same index), so index-based
// selection survives that; it only resets on add/delete, which is
// handled explicitly below.
function DockedMapEditorInner<P extends LatLng>({
  points, mapCenter, mapZoom, renderOverlay, markerIcon,
  panelTitle, panelExtraFields, onCommitPoint, onAddPoint, onDeletePoint, referenceMarkers,
}: DockedMapEditorProps<P>, ref: ForwardedRef<DockedMapEditorHandle>) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [tapPlaceMode, setTapPlaceMode] = useState(false)
  // The index of a just-added, not-yet-placed point (still sitting wherever
  // onAddPoint put it) -- cleared once it's committed once, or once the user
  // moves on to something else without ever placing it.
  const [placingIndex, setPlacingIndex] = useState<number | null>(null)
  const selected = selectedIndex !== null ? points[selectedIndex] ?? null : null
  const zoom = mapZoom ?? (points.length > 0 ? 15 : 12)

  const selectOther = (i: number | null) => {
    if (i !== placingIndex) setPlacingIndex(null)
    setSelectedIndex(i)
  }

  const commit = (index: number, lat: number, lng: number) => {
    const isPlacement = index === placingIndex
    const result = onCommitPoint(index, lat, lng, isPlacement)
    if (isPlacement) setPlacingIndex(null)
    if (typeof result === 'number' && result !== index) setSelectedIndex(result)
  }

  const handleMapClick = (ll: LatLng) => {
    if (!tapPlaceMode || selectedIndex === null) return
    commit(selectedIndex, ll.lat, ll.lng)
  }

  const handleAdd = () => {
    const newIndex = onAddPoint(mapCenter[0], mapCenter[1])
    setSelectedIndex(newIndex)
    setPlacingIndex(newIndex)
    setTapPlaceMode(true) // new point starts at the map center -- likely needs placing
  }

  useImperativeHandle(ref, () => ({ triggerAdd: handleAdd }))

  return (
    <div className="relative w-full h-full">
      <LeafletMap center={mapCenter} zoom={zoom}>
        <ClickHandler active={tapPlaceMode && selectedIndex !== null} onClick={handleMapClick} />
        {renderOverlay?.(points)}
        {points.map((p, i) => (
          <Marker
            key={i}
            position={[p.lat, p.lng]}
            icon={markerIcon(p, i, i === selectedIndex)}
            draggable
            eventHandlers={{
              click: () => { selectOther(i); setTapPlaceMode(false) },
              dragend: (e) => {
                const ll = (e.target as L.Marker).getLatLng()
                commit(i, ll.lat, ll.lng)
              },
            }}
          />
        ))}
        {referenceMarkers?.map((m) => (
          <Marker key={m.key} position={[m.lat, m.lng]} icon={m.icon}>
            {m.label && <Popup>{m.label}</Popup>}
          </Marker>
        ))}
      </LeafletMap>

      {selected && selectedIndex !== null && (
        <div className="absolute bottom-4 right-4 z-[1000]">
          <MapEditPanel
            key={selectedIndex}
            title={panelTitle(selected, selectedIndex)}
            lat={selected.lat}
            lng={selected.lng}
            onSave={(lat, lng) => { commit(selectedIndex, lat, lng); selectOther(null); setTapPlaceMode(false) }}
            onDelete={onDeletePoint ? () => { onDeletePoint(selectedIndex); selectOther(null); setTapPlaceMode(false) } : undefined}
            onCancel={() => { selectOther(null); setTapPlaceMode(false) }}
            externalCoord={{ lat: selected.lat, lng: selected.lng }}
            tapPlaceActive={tapPlaceMode}
            onToggleTapPlace={() => setTapPlaceMode((v) => !v)}
            extraFields={panelExtraFields?.(selected, selectedIndex)}
          />
        </div>
      )}
    </div>
  )
}

// forwardRef erases the generic <P> by default; this cast restores it so
// callers still get a properly-typed `points`/`markerIcon`/etc. instead of
// `unknown`, same trick as react-hook-form and others use for the same problem.
export const DockedMapEditor = forwardRef(DockedMapEditorInner) as <P extends LatLng>(
  props: DockedMapEditorProps<P> & { ref?: ForwardedRef<DockedMapEditorHandle> }
) => ReactElement
